import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import {
  BrowserAuthError,
  InteractionStatus,
  PromptValue,
  type AccountInfo,
} from "@azure/msal-browser";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { setAccessTokenProvider, setAuthRequired } from "../api/client.js";
import { flushAll } from "../api/dirtyTracker.js";
import { waitForBrandIntro } from "../components/shell/brandIntro.js";
import { entraWebConfig, loginRequest, msalInstance } from "./msal.js";
import { t } from "@mmm/ui";
/** Short, best-effort window to flush dirty drafts before a full-page auth
 * redirect discards all in-memory state. A dead API must not block sign-in
 * recovery, so this is capped — see `getAccessToken` below. */
const PRE_REDIRECT_FLUSH_TIMEOUT_MS = 3000;

// Module-level pointer to the mounted provider's signIn(), mirroring the
// pushToast()/setAccessTokenProvider() escape-hatch pattern already used by
// ToastProvider.tsx/client.ts — lets the toast pushed for AuthRequiredError
// (commands.ts) offer a working "Sign in" action without needing a hook.
let externalSignIn: (() => Promise<void>) | null = null;

// eslint-disable-next-line react-refresh/only-export-components
export function triggerSignIn(): void {
  void externalSignIn?.();
}

export interface AuthAccount {
  /** Display name for the signed-in user (falls back to the email local part). */
  name: string;
  /** Email / UPN, or "" when the provider doesn't supply one. */
  email: string;
  /** 1-2 letter avatar initials derived from the display name. */
  initials: string;
}

interface AuthContextValue {
  signedOut: boolean;
  isReady: boolean;
  account: AuthAccount | null;
  signOut: () => Promise<void>;
  signIn: () => Promise<void>;
  signUp: () => Promise<void>;
  getAccessToken: (request?: { forceRefresh?: boolean }) => Promise<string | null>;
}

const SIGNED_OUT_KEY = "mmm.signedOut";
const AuthContext = createContext<AuthContextValue | null>(null);

function readInitialSignedOut(): boolean {
  return localStorage.getItem(SIGNED_OUT_KEY) === "1";
}

function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Exported so a surface showing who is signed in can build the same shape from
 * the API's session -- the identity a request was actually authorized under --
 * rather than from MSAL's cached account, which can carry older claims.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function buildAccount(name: string | undefined, email: string | undefined): AuthAccount {
  // Plain helper (not a component), so it reads t from the store lazily; the
  // fallback only fires when the identity provider supplies neither a name
  // nor an email, and it re-derives on the provider's next render.
  const displayName = name?.trim() || email?.split("@")[0] || t("settings.accountFallbackName");
  return { name: displayName, email: email ?? "", initials: initialsFrom(displayName) };
}

function safeGetActiveAccount(instance: ReturnType<typeof useMsal>["instance"]): AccountInfo | null {
  try {
    return instance.getActiveAccount();
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (msalInstance && entraWebConfig && loginRequest) {
    return (
      <MsalProvider instance={msalInstance}>
        <EntraAuthProvider>{children}</EntraAuthProvider>
      </MsalProvider>
    );
  }

  return <DevAuthProvider>{children}</DevAuthProvider>;
}

function DevAuthProvider({ children }: { children: ReactNode }) {
  const [signedOut, setSignedOut] = useState<boolean>(readInitialSignedOut);

  const getAccessToken = useCallback(async () => null, []);

  useEffect(() => setAccessTokenProvider(getAccessToken), [getAccessToken]);
  // Dev mode has no tokens by design — requests go out unauthenticated, so
  // fetchWithAuth must never throw AuthRequiredError here.
  useEffect(() => setAuthRequired(false), []);

  const signOut = useCallback(async () => {
    localStorage.setItem(SIGNED_OUT_KEY, "1");
    setSignedOut(true);
  }, []);

  const signIn = useCallback(async () => {
    localStorage.setItem(SIGNED_OUT_KEY, "0");
    setSignedOut(false);
  }, []);

  const signUp = useCallback(async () => {
    localStorage.setItem(SIGNED_OUT_KEY, "0");
    setSignedOut(false);
  }, []);

  useEffect(() => {
    externalSignIn = signIn;
    return () => {
      if (externalSignIn === signIn) externalSignIn = null;
    };
  }, [signIn]);

  const account = buildAccount("Dev User", "dev@local");

  return (
    <AuthContext.Provider value={{ signedOut, isReady: true, account, signOut, signIn, signUp, getAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
}

function EntraAuthProvider({ children }: { children: ReactNode }) {
  const { accounts, instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isReady = inProgress === InteractionStatus.None;
  const activeAccount = isReady ? safeGetActiveAccount(instance) ?? accounts[0] ?? null : accounts[0] ?? null;
  // MSAL reports an account as signed in while its cached tokens are dead, so
  // this tracks what silent acquisition actually found. See getAccessToken.
  const [sessionExpired, setSessionExpired] = useState(false);
  const interactiveRecoveryStarted = useRef(false);

  useEffect(() => {
    if (!isReady || !accounts[0]) return;
    if (!safeGetActiveAccount(instance)) instance.setActiveAccount(accounts[0]);
  }, [accounts, instance, isReady]);

  // Every redirect below ends in a full-page navigation that tears down
  // whatever is on screen. When one fires while the brand splash is still
  // drawing its logo — the public site's Sign In / Sign Up handoff lands on
  // /login and redirects without a second click — the intro is cut mid-stroke
  // and, being once-per-visit, never seen at all. So each redirect lets the
  // sequence finish first; once it has, the wait is a no-op, which is the
  // usual case for a button pressed from inside the workspace. See
  // components/shell/brandIntro.ts.
  const startAuthRedirect = useCallback(async (prompt?: string) => {
    if (!loginRequest) return;
    await waitForBrandIntro();
    await instance.loginRedirect({
      ...loginRequest,
      ...(prompt ? { prompt } : {}),
      redirectStartPage: `${window.location.origin}/home`,
    });
  }, [instance]);

  const signIn = useCallback(async () => {
    await startAuthRedirect();
  }, [startAuthRedirect]);

  const signUp = useCallback(async () => {
    await startAuthRedirect(PromptValue.CREATE);
  }, [startAuthRedirect]);

  const signOut = useCallback(async () => {
    await waitForBrandIntro();
    await instance.logoutRedirect({ postLogoutRedirectUri: `${window.location.origin}/signed-out` });
  }, [instance]);

  const getAccessToken = useCallback(async (request?: { forceRefresh?: boolean }) => {
    if (!entraWebConfig || !loginRequest) return null;

    const account = safeGetActiveAccount(instance) ?? accounts[0];
    if (!account) {
      return null;
    }

    try {
      const result = await instance.acquireTokenSilent({
        account,
        scopes: [entraWebConfig.apiScope],
        forceRefresh: request?.forceRefresh,
      });
      setSessionExpired(false);
      return result.accessToken;
    } catch (error) {
      if (error instanceof BrowserAuthError && error.errorCode === "uninitialized_public_client_application") {
        return null;
      }

      // Everything else means this cached account cannot produce a token, and
      // only interactive sign-in recovers. It is deliberately not narrowed to
      // InteractionRequiredAuthError: when Entra answers the hidden iframe
      // with an interrupt page the iframe never navigates back, so MSAL gives
      // up with a BrowserAuthError (monitor_window_timeout) instead. Treating
      // that as an unexpected error — rethrowing it — is what left the app
      // retrying a doomed silent call behind the splash indefinitely, because
      // MSAL still reports a stale account as signed in.
      //
      // One automatic redirect per page load. If it comes back and silent auth
      // still fails, fall through to the sign-in screen rather than bouncing
      // the browser through Entra again.
      if (!interactiveRecoveryStarted.current && inProgress === InteractionStatus.None) {
        interactiveRecoveryStarted.current = true;
        try {
          // WP-12: a full-page redirect is about to discard all in-memory
          // state (drafts included) — give pending saves a short, bounded
          // window to land first. Best effort only: flushAll() never rejects,
          // and a dead API must not block sign-in recovery, so this proceeds
          // to the redirect regardless of the outcome.
          await flushAll(PRE_REDIRECT_FLUSH_TIMEOUT_MS);
          await waitForBrandIntro();
          // No `redirectStartPage` here (unlike signIn/signUp below, which
          // deliberately enter through the /home last-viewed-page resolver) — MSAL's default
          // navigateToLoginRequestUrl behaviour returns the user to the page
          // they were already on once the redirect round-trip completes.
          await instance.acquireTokenRedirect({
            account,
            scopes: [entraWebConfig.apiScope],
          });
          return null;
        } catch (redirectError) {
          console.error("Could not start interactive sign-in after a silent token failure.", redirectError);
        }
      }

      // Report the session as signed out so the router sends the user to the
      // sign-in screen. A stale account otherwise keeps `isAuthenticated`
      // true, leaving them on a workspace whose every request fails.
      console.error("Silent token acquisition failed; treating the session as expired.", error);
      setSessionExpired(true);
      return null;
    }
  }, [accounts, inProgress, instance]);

  useEffect(() => setAccessTokenProvider(getAccessToken), [getAccessToken]);
  useEffect(() => setAuthRequired(true), []);

  useEffect(() => {
    externalSignIn = signIn;
    return () => {
      if (externalSignIn === signIn) externalSignIn = null;
    };
  }, [signIn]);

  const account = isAuthenticated && activeAccount ? buildAccount(activeAccount.name, activeAccount.username) : null;

  return (
    <AuthContext.Provider
      value={{
        signedOut: isReady && (!isAuthenticated || !activeAccount || sessionExpired),
        isReady,
        account,
        signOut,
        signIn,
        signUp,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
